import { useState, type ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Fuel as FuelIcon, Gauge, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import { FuelFormModal } from '../components/forms/FuelFormModal';
import { CarSelect } from '../components/forms/EntitySelects';
import { Badge, type BadgeVariant } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar, SearchInput } from '../components/ui/FilterBar';
import { Checkbox, Select } from '../components/ui/Input';
import { ConfirmDialog } from '../components/ui/Modal';
import { Tabs } from '../components/ui/Tabs';
import { useDeleteFuel, useFuelImport, useFuelPreview, useFuelSummary, useFuelTransactions } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import { errorMessage } from '../services/api';
import { toast } from '../store';
import type { DrivingRating, FuelCarSummary, FuelImportError, FuelImportPreview, FuelImportResult, FuelSummary, FuelTransaction } from '../types';
import {
  cn, countryName, driverName, FUEL_CATEGORY_LABELS, FUEL_LABELS, FUEL_PAYMENT_LABELS, formatCurrency, formatDate, formatFileSize, formatNumber,
} from '../utils/helpers';
import { locale, t, tx } from '../i18n';

const THIS_YEAR = new Date().getFullYear();

type Filters = { tab: string; year: string; period: string; car_id: string; category: string; source: string; search: string; page: string };

/** First and last day of the chosen year, quarter (q1–q4) or month (01–12). */
function periodRange(year: number | undefined, period: string): { date_from?: string; date_to?: string } {
  if (!year) return {};
  let first = 1;
  let last = 12;
  if (/^q[1-4]$/.test(period)) {
    first = (Number(period[1]) - 1) * 3 + 1;
    last = first + 2;
  } else if (/^\d{2}$/.test(period)) {
    first = last = Number(period);
  }
  const lastDay = new Date(year, last, 0).getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return { date_from: `${year}-${pad(first)}-01`, date_to: `${year}-${pad(last)}-${pad(lastDay)}` };
}

const formatLiters = (value: number | null | undefined, digits = 1) => (value == null ? '–' : `${formatNumber(value, digits)} l`);
const formatConsumption = (value: number | null) => (value === null ? '–' : `${formatNumber(value, 1)} l/100 km`);

const RATING: Record<DrivingRating, { variant: BadgeVariant; label: () => string }> = {
  economical: { variant: 'success', label: () => t('Hospodárna jazda') },
  elevated: { variant: 'warning', label: () => t('Zvýšená spotreba') },
  aggressive: { variant: 'danger', label: () => t('Agresívna jazda') },
};

function RatingBadge({ car }: { car: FuelCarSummary }) {
  if (!car.rating || car.deviation_pct == null) return null;
  const { variant, label } = RATING[car.rating];
  const pct = `${car.deviation_pct > 0 ? '+' : ''}${formatNumber(car.deviation_pct, 0)} %`;
  return (
    <Badge variant={variant} dot title={t('Oproti spotrebe podľa technického preukazu')}>
      {label()} · {pct}
    </Badge>
  );
}

export function Fuel() {
  const [filters, setFilters] = useUrlFilters<Filters>({
    tab: 'overview', year: String(THIS_YEAR), period: '', car_id: '', category: '', source: '', search: '', page: '1',
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<FuelTransaction | null>(null);
  const year = toInt(filters.year);
  const range = periodRange(year, filters.period);
  const { data: summary } = useFuelSummary(range);

  const monthName = (m: number) => new Date(2000, m - 1, 1).toLocaleDateString(locale(), { month: 'long' });
  const periodOptions = [
    { value: 'q1', label: t('1. štvrťrok') },
    { value: 'q2', label: t('2. štvrťrok') },
    { value: 'q3', label: t('3. štvrťrok') },
    { value: 'q4', label: t('4. štvrťrok') },
    ...Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1).padStart(2, '0'), label: monthName(i + 1) })),
  ];

  return (
    <>
      <PageHeader
        title={t('Palivo')}
        subtitle={t('Tankovanie zo Shell kariet aj v hotovosti – náklady, litre a spotreba podľa GPS kilometrov')}
        actions={
          <>
            <Select
              aria-label={t('Rok')}
              wrapperClassName="w-32"
              placeholder={t('Všetky roky')}
              options={(summary?.years ?? [THIS_YEAR]).map((y) => ({ value: String(y), label: String(y) }))}
              value={filters.year}
              onChange={(e) => setFilters({ year: e.target.value, period: e.target.value ? filters.period : '', page: '1' })}
            />
            {year && (
              <Select
                aria-label={t('Obdobie')}
                wrapperClassName="w-40"
                placeholder={t('Celý rok')}
                options={periodOptions}
                value={filters.period}
                onChange={(e) => setFilters({ period: e.target.value, page: '1' })}
              />
            )}
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />  {t('Pridať tankovanie')}
            </Button>
          </>
        }
      />
      <Tabs
        className="mb-4"
        value={filters.tab}
        onChange={(tab) => setFilters({ tab })}
        tabs={[
          { value: 'overview', label: t('Prehľad') },
          { value: 'transactions', label: t('Tankovania') },
          { value: 'import', label: t('Import zo Shell karty') },
        ]}
      />
      {filters.tab === 'import' ? (
        <ImportWizard onDone={() => setFilters({ tab: 'overview' })} />
      ) : filters.tab === 'transactions' ? (
        <TransactionList filters={filters} setFilters={setFilters} range={range} onEdit={setEditing} />
      ) : summary ? (
        <Overview summary={summary} onSelectCar={(id) => setFilters({ tab: 'transactions', car_id: String(id), page: '1' })} />
      ) : null}
      <FuelFormModal isOpen={creating} onClose={() => setCreating(false)} />
      <FuelFormModal isOpen={!!editing} transaction={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function Tile({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <Card padding="sm">
      <p className="text-sm text-secondary-500">{label}</p>
      <p className="mt-1 truncate text-2xl font-bold tabular-nums text-secondary-900">{value}</p>
      {note && <p className="mt-0.5 truncate text-xs text-secondary-500">{note}</p>}
    </Card>
  );
}

// ---------------------------------------------------------------- overview
function Overview({ summary, onSelectCar }: { summary: FuelSummary; onSelectCar: (id: number) => void }) {
  if (!summary.count) {
    return (
      <Card>
        <EmptyState icon={<FuelIcon className="h-10 w-10" />} title={t('V tomto období nie je žiadne tankovanie')}>
          {t('Importujte export transakcií zo Shell karty alebo pridajte tankovanie ručne.')}
        </EmptyState>
      </Card>
    );
  }
  const consumptions = summary.cars.map((c) => c.consumption).filter((v): v is number => v !== null);
  const maxConsumption = consumptions.length > 1 ? Math.max(...consumptions) : null;
  const maxLiters = Math.max(...summary.cars.map((c) => c.liters), 1);

  const columns: Column<FuelCarSummary>[] = [
    {
      key: 'car',
      header: t('Vozidlo'),
      render: (c) => (
        <div>
          <p className="font-medium text-secondary-900">{c.license_plate}</p>
          <p className="text-xs text-secondary-500">
            {c.brand} {c.model}
            {c.fuel_type && ` · ${FUEL_LABELS[c.fuel_type]}`}
          </p>
        </div>
      ),
    },
    {
      key: 'liters',
      header: t('Natankované'),
      render: (c) => (
        <div className="min-w-[7rem] tabular-nums">
          <p>{formatLiters(c.liters)}</p>
          <div className="mt-1 h-1.5 rounded-full bg-secondary-100">
            <div className="h-1.5 rounded-full bg-orange-500" style={{ width: `${(c.liters / maxLiters) * 100}%` }} />
          </div>
        </div>
      ),
    },
    { key: 'fuel_net', header: t('Palivo bez DPH'), className: 'tabular-nums font-medium', render: (c) => formatCurrency(c.fuel_net) },
    { key: 'price', header: t('Priem. cena / l'), className: 'tabular-nums', render: (c) => (c.price_per_liter === null ? '–' : `${formatNumber(c.price_per_liter, 3)} €`) },
    { key: 'km', header: t('Km podľa GPS'), className: 'tabular-nums', render: (c) => (c.km === null ? '–' : formatNumber(c.km)) },
    {
      key: 'consumption',
      header: t('Spotreba'),
      className: 'tabular-nums whitespace-nowrap',
      render: (c) =>
        c.consumption !== null && c.consumption === maxConsumption ? (
          <Badge variant="warning" title={t('Najvyššia spotreba')}>{formatConsumption(c.consumption)}</Badge>
        ) : (
          formatConsumption(c.consumption)
        ),
    },
    {
      key: 'rating',
      header: t('Štýl jazdy'),
      className: 'whitespace-nowrap',
      render: (c) =>
        c.rating ? (
          <RatingBadge car={c} />
        ) : (
          <span className="text-xs text-secondary-400">{c.official_consumption ? t('chýbajú GPS km') : t('bez spotreby podľa TP')}</span>
        ),
    },
    { key: 'per_km', header: t('Palivo € / km'), className: 'tabular-nums', render: (c) => (c.fuel_cost_per_km === null ? '–' : formatNumber(c.fuel_cost_per_km, 2)) },
    { key: 'other', header: t('AdBlue a iné'), className: 'tabular-nums', render: (c) => formatCurrency(c.other_net) },
    {
      key: 'warn',
      header: <span className="sr-only">{t('Upozornenia')}</span>,
      render: (c) =>
        c.mismatches > 0 && (
          <Badge variant="danger" title={t('Tankovanie iného paliva, než má vozidlo zapísané')}>
            <AlertTriangle className="h-3 w-3" /> {t('{count}× iné palivo', { count: c.mismatches })}
          </Badge>
        ),
    },
  ];

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Tile label={t('Palivo bez DPH')} value={formatCurrency(summary.fuel_net)} note={t('s DPH spolu {amount}', { amount: formatCurrency(summary.total_gross) })} />
        <Tile label={t('Natankované')} value={formatLiters(summary.liters, 0)} note={t('{count} transakcií', { count: formatNumber(summary.count) })} />
        <Tile label={t('Priemerná cena')} value={summary.price_per_liter === null ? '–' : `${formatNumber(summary.price_per_liter, 3)} €/l`} note={t('s DPH, ako na pumpe')} />
        <Tile
          label={t('Priemerná spotreba')}
          value={summary.consumption === null ? '–' : `${formatNumber(summary.consumption, 1)} l`}
          note={summary.consumption === null ? t('chýbajú GPS kilometre') : t('na 100 km podľa GPS')}
        />
        <Tile label={t('AdBlue a nákupy na pumpe')} value={formatCurrency(summary.adblue_net + summary.other_net)} note={t('AdBlue {amount}', { amount: formatCurrency(summary.adblue_net) })} />
      </div>

      <DrivingStyle cars={summary.cars} />

      <Card padding="none" className="mt-6">
        <div className="p-5 pb-0">
          <CardHeader
            title={t('Porovnanie vozidiel')}
            subtitle={t('Spotreba = litre paliva ÷ GPS kilometre v mesiacoch, ktoré majú oboje. Kliknite na vozidlo pre jeho tankovania.')}
          />
        </div>
        <DataTable data={summary.cars} columns={columns} rowKey={(c) => c.car_id} onRowClick={(c) => onSelectCar(c.car_id)} />
      </Card>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title={t('Podľa mesiacov')} subtitle={t('Palivo bez DPH a natankované litre')} />
          <MonthRows rows={summary.by_month} />
        </Card>
        <div className="space-y-6">
          <Card>
            <CardHeader title={t('Podľa krajiny')} />
            <AmountRows rows={Object.entries(summary.by_country).map(([code, amount]) => ({ key: code, label: code === '?' ? t('Neznáma') : `${code} – ${countryName(code)}`, amount }))} />
          </Card>
          <Card>
            <CardHeader title={t('Podľa platby')} />
            <AmountRows rows={Object.entries(summary.by_payment).map(([key, amount]) => ({ key, label: FUEL_PAYMENT_LABELS[key as keyof typeof FUEL_PAYMENT_LABELS] ?? key, amount }))} />
          </Card>
        </div>
      </div>
    </>
  );
}

/** Refuelled litres vs. what the technical file expects for the GPS km, shown as full tanks too. */
function DrivingStyle({ cars }: { cars: FuelCarSummary[] }) {
  const rated = cars.filter((c) => c.rating).sort((a, b) => (b.deviation_pct ?? 0) - (a.deviation_pct ?? 0));
  const missing = cars.filter((c) => !c.official_consumption);
  const tanks = (value: number | null | undefined) => (value == null ? '' : ` = ${t('{count} nádrže', { count: formatNumber(value, 1) })}`);
  return (
    <Card className="mt-6">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary-600" />
            {t('Štýl jazdy podľa technického preukazu')}
          </span>
        }
        subtitle={t('Koľko sa natankovalo oproti tomu, koľko by auto podľa TP spotrebovalo na najazdené GPS kilometre. Do +15 % hospodárna jazda, do +30 % zvýšená spotreba, nad 30 % agresívna jazda.')}
      />
      {rated.length > 0 ? (
        <ul className="divide-y divide-secondary-100">
          {rated.map((c) => (
            <li key={c.car_id} className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3">
              <div className="min-w-[10rem]">
                <Link to={`/cars/${c.car_id}`} className="font-mono font-medium text-primary-700 hover:underline">
                  {c.license_plate}
                </Link>
                <p className="text-xs text-secondary-500">
                  {c.brand} {c.model} · {t('TP {value} l/100 km', { value: formatNumber(c.official_consumption, 1) })}
                  {c.tank_capacity ? ` · ${t('nádrž {liters} l', { liters: formatNumber(c.tank_capacity) })}` : ''}
                </p>
              </div>
              <dl className="grid flex-1 grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-secondary-500">{t('Očakávané podľa TP')}</dt>
                  <dd className="tabular-nums">
                    {formatLiters(c.expected_liters, 0)}
                    {tanks(c.expected_tanks)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-secondary-500">{t('Natankované')}</dt>
                  <dd className="tabular-nums">
                    {formatLiters(c.matched_liters, 0)}
                    {tanks(c.actual_tanks)} <span className="text-secondary-500">({t('{count}× tankovanie', { count: c.refuel_count })})</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-secondary-500">{t('Reálna spotreba')}</dt>
                  <dd className="tabular-nums">{formatConsumption(c.consumption)}</dd>
                </div>
              </dl>
              <RatingBadge car={c} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-secondary-500">{t('Zatiaľ nie je čo hodnotiť – vozidlá potrebujú spotrebu podľa TP, tankovania aj GPS kilometre v tom istom mesiaci.')}</p>
      )}
      {missing.length > 0 && (
        <p className="mt-3 text-xs text-secondary-500">
          {t('Doplňte spotrebu podľa TP a objem nádrže v karte vozidla:')}{' '}
          {missing.map((c, i) => (
            <span key={c.car_id}>
              {i > 0 && ', '}
              <Link to={`/cars/${c.car_id}`} className="text-primary-600 hover:underline">
                {c.license_plate}
              </Link>
            </span>
          ))}
        </p>
      )}
    </Card>
  );
}

function MonthRows({ rows }: { rows: FuelSummary['by_month'] }) {
  const max = Math.max(...rows.map((r) => r.fuel_net), 1);
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => {
        const [y, m] = r.month.split('-').map(Number);
        const label = new Date(y, m - 1, 1).toLocaleDateString(locale(), { month: 'long', year: 'numeric' });
        return (
          <li key={r.month} title={`${label}: ${formatCurrency(r.fuel_net)} · ${formatLiters(r.liters)}`}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className="text-secondary-700">{label}</span>
              <span className="shrink-0 tabular-nums text-secondary-900">
                {formatCurrency(r.fuel_net)} <span className="text-xs text-secondary-500">· {formatLiters(r.liters, 0)}</span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-orange-100">
              <div className="h-2 rounded-full bg-orange-600" style={{ width: `${Math.max((r.fuel_net / max) * 100, 1.5)}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function AmountRows({ rows }: { rows: { key: string; label: string; amount: number }[] }) {
  return (
    <ul className="space-y-1.5 text-sm">
      {rows.map((r) => (
        <li key={r.key} className="flex justify-between gap-3">
          <span className="truncate text-secondary-700">{r.label}</span>
          <span className="shrink-0 tabular-nums text-secondary-900">{formatCurrency(r.amount)}</span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- transactions
function TransactionList({
  filters, setFilters, range, onEdit,
}: {
  filters: Filters;
  setFilters: (p: Partial<Filters>) => void;
  range: { date_from?: string; date_to?: string };
  onEdit: (t: FuelTransaction) => void;
}) {
  const page = toInt(filters.page, 1)!;
  const { data, isLoading, isFetching } = useFuelTransactions({
    ...range,
    car_id: toInt(filters.car_id),
    category: filters.category,
    source: filters.source,
    search: filters.search,
    page,
    page_size: 50,
  });
  const remove = useDeleteFuel();
  const [deleting, setDeleting] = useState<FuelTransaction | null>(null);

  const columns: Column<FuelTransaction>[] = [
    {
      key: 'date',
      header: t('Dátum'),
      className: 'whitespace-nowrap',
      render: (f) => (
        <div>
          <p>{formatDate(f.transaction_date)}</p>
          {f.transaction_time && <p className="text-xs text-secondary-500">{f.transaction_time.slice(0, 5)}</p>}
        </div>
      ),
    },
    {
      key: 'car',
      header: t('Vozidlo'),
      render: (f) => (
        <Link to={`/cars/${f.car_id}`} className="whitespace-nowrap font-mono font-medium text-primary-700 hover:underline">
          {f.car?.license_plate}
        </Link>
      ),
    },
    {
      key: 'product',
      header: t('Produkt'),
      render: (f) => (
        <div className="text-sm">
          <p className="flex flex-wrap items-center gap-1.5">
            {f.product}
            {f.category !== 'fuel' && <Badge>{f.category === 'adblue' ? 'AdBlue' : t('Ostatné')}</Badge>}
            {f.fuel_mismatch && (
              <Badge variant="danger" title={t('Vozidlo má zapísané iné palivo')}>
                <AlertTriangle className="h-3 w-3" /> {t('iné palivo')}
              </Badge>
            )}
          </p>
          <p className="text-xs text-secondary-500">{f.driver ? driverName(f.driver) : f.person_name}</p>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: t('Množstvo'),
      className: 'text-right tabular-nums whitespace-nowrap',
      headerClassName: 'text-right',
      render: (f) => (
        <div>
          <p>{f.quantity == null ? '–' : formatLiters(f.quantity, 2)}</p>
          {f.unit_price != null && <p className="text-xs text-secondary-500">{formatNumber(f.unit_price, 3)} €/l</p>}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('Bez DPH'),
      className: 'text-right tabular-nums whitespace-nowrap',
      headerClassName: 'text-right',
      render: (f) => (
        <div>
          <p className="font-medium">{formatCurrency(f.amount_net)}</p>
          {f.amount_gross != null && <p className="text-xs text-secondary-500">{t('s DPH {amount}', { amount: formatCurrency(f.amount_gross) })}</p>}
        </div>
      ),
    },
    {
      key: 'place',
      header: t('Kde'),
      render: (f) => (
        <div className="max-w-[14rem] text-sm">
          <p className="truncate">
            {f.country && <Badge variant="primary">{f.country}</Badge>} {f.city || f.station}
          </p>
          {f.city && f.station && <p className="truncate text-xs text-secondary-500">{f.station}</p>}
        </div>
      ),
    },
    {
      key: 'payment',
      header: t('Platba'),
      render: (f) => (
        <div className="whitespace-nowrap text-sm">
          <p>{FUEL_PAYMENT_LABELS[f.payment_method]}</p>
          {f.source === 'card' && (
            <p className="text-xs text-secondary-500">
              {f.invoiced ? t('faktúra {invoice}', { invoice: f.invoice_number ?? '' }) : t('zatiaľ nevyfakturované')}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (f) => (
        <>
          <button className="icon-btn" onClick={() => onEdit(f)} title={t('Upraviť')} aria-label={t('Upraviť')}>
            <Pencil className="h-4 w-4" />
          </button>
          <button className="icon-btn-danger" onClick={() => setDeleting(f)} title={t('Zmazať')} aria-label={t('Zmazať')}>
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      ),
    },
  ];

  return (
    <Card padding="none">
      <FilterBar>
        <SearchInput value={filters.search} onChange={(search) => setFilters({ search, page: '1' })} placeholder={t('Produkt, stanica, meno, faktúra…')} />
        <CarSelect label={t('Vozidlo')} wrapperClassName="sm:w-56" placeholder={t('Všetky vozidlá')} value={filters.car_id} onChange={(e) => setFilters({ car_id: e.target.value, page: '1' })} />
        <Select
          label={t('Druh')}
          wrapperClassName="sm:w-44"
          placeholder={t('Všetko')}
          options={Object.entries(FUEL_CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
          value={filters.category}
          onChange={(e) => setFilters({ category: e.target.value, page: '1' })}
        />
        <Select
          label={t('Zdroj')}
          wrapperClassName="sm:w-44"
          placeholder={t('Všetky')}
          options={[
            { value: 'card', label: t('Shell karta (import)') },
            { value: 'manual', label: t('Zadané ručne') },
          ]}
          value={filters.source}
          onChange={(e) => setFilters({ source: e.target.value, page: '1' })}
        />
      </FilterBar>
      {data && (
        <p className="border-b border-secondary-100 px-4 py-2 text-sm text-secondary-600">
          {tx('{count} transakcií · palivo {liters} · bez DPH {net} · s DPH {gross}', {
            count: formatNumber(data.total),
            liters: <strong>{formatLiters(data.total_liters, 0)}</strong>,
            net: <strong>{formatCurrency(data.total_net)}</strong>,
            gross: formatCurrency(data.total_gross),
          })}
        </p>
      )}
      <DataTable
        data={data?.transactions}
        columns={columns}
        rowKey={(f) => f.id}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyMessage={<EmptyState icon={<FuelIcon className="h-10 w-10" />} title={t('Žiadne tankovanie nezodpovedá filtru')} />}
        pagination={data && { page, pageSize: 50, total: data.total, onPageChange: (p) => setFilters({ page: String(p) }) }}
      />
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        loading={remove.isPending}
        title={t('Zmazať tankovanie')}
        confirmText={t('Zmazať')}
        message={
          deleting &&
          (deleting.source === 'card'
            ? t('Zmazať transakciu {product} {amount} ({date})? Pri ďalšom importe toho istého obdobia sa znova pridá.', {
                product: deleting.product, amount: formatCurrency(deleting.amount_net), date: formatDate(deleting.transaction_date),
              })
            : t('Zmazať tankovanie {product} {amount} ({date})?', {
                product: deleting.product, amount: formatCurrency(deleting.amount_net), date: formatDate(deleting.transaction_date),
              }))
        }
        onConfirm={async () => {
          try {
            await remove.mutateAsync(deleting!.id);
            toast.success(t('Tankovanie bolo zmazané'));
          } catch (err) {
            toast.error(errorMessage(err));
          }
          setDeleting(null);
        }}
      />
    </Card>
  );
}

// ---------------------------------------------------------------- import
function ImportWizard({ onDone }: { onDone: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<FuelImportPreview | null>(null);
  const [result, setResult] = useState<FuelImportResult | null>(null);
  const [createMissing, setCreateMissing] = useState(false);
  const previewMutation = useFuelPreview();
  const importMutation = useFuelImport();

  const reset = () => {
    setFiles([]);
    setPreview(null);
    setResult(null);
  };

  const loadPreview = async (selected: File[]) => {
    if (!selected.length) return reset();
    try {
      setPreview(await previewMutation.mutateAsync(selected));
    } catch (err) {
      toast.error(errorMessage(err, t('Súbory sa nepodarilo spracovať')));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv', '.CSV'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx', '.XLSX'],
    },
    multiple: true,
    onDrop: (accepted, rejected) => {
      if (rejected.length) toast.error(t('{rejected} súbor(ov) nie je vo formáte CSV ani XLSX', { rejected: rejected.length }));
      if (!accepted.length) return;
      const next = [...new Map([...files, ...accepted].map((f) => [f.name, f])).values()];
      setFiles(next);
      setResult(null);
      loadPreview(next);
    },
  });

  const removeFile = (name: string) => {
    const next = files.filter((f) => f.name !== name);
    setFiles(next);
    loadPreview(next);
  };

  const runImport = async () => {
    try {
      const data = await importMutation.mutateAsync({ files, createMissing });
      setResult(data);
      setPreview(null);
      toast.success(t('Import dokončený – nové: {created}, aktualizované: {updated}', { created: data.created, updated: data.updated }));
    } catch (err) {
      toast.error(errorMessage(err, t('Import zlyhal')));
    }
  };

  if (result) {
    return (
      <Card>
        <div className="flex flex-col items-center py-6 text-center">
          <CheckCircle2 className={cn('mb-3 h-12 w-12', result.created || result.updated ? 'text-green-500' : 'text-secondary-300')} />
          <h2 className="text-lg font-semibold">{t('Import dokončený')}</h2>
          <div className="mt-6 grid w-full max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t('Nové transakcie')} value={result.created} tone="text-green-700" />
            <Stat label={t('Aktualizované')} value={result.updated} />
            <Stat label={t('Bez zmeny (už importované)')} value={result.unchanged} tone="text-secondary-600" />
            <Stat label={t('Preskočené')} value={result.skipped} tone={result.skipped ? 'text-amber-600' : undefined} />
          </div>
          {result.skipped_plates.length > 0 && (
            <p className="mt-4 max-w-2xl rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              {t('Preskočené – vozidlá nie sú v systéme: {plates}', { plates: result.skipped_plates.map((p) => `${p.license_plate} (${p.count}×)`).join(', ') })}
            </p>
          )}
          {result.created_cars.length > 0 && (
            <p className="mt-4 max-w-2xl rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              {t('Novo založené vozidlá (doplňte značku a model): {cars}', { cars: result.created_cars.join(', ') })}
            </p>
          )}
          <ErrorList errors={result.errors} />
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={reset}>
              <Upload className="h-4 w-4" />  {t('Importovať ďalší súbor')}
            </Button>
            <Button onClick={onDone}>{t('Zobraziť prehľad')}</Button>
          </div>
        </div>
      </Card>
    );
  }

  const dropzone = (
    <div
      {...getRootProps()}
      className={cn(
        'cursor-pointer rounded-xl border-2 border-dashed text-center transition-colors',
        files.length ? 'p-4' : 'p-10',
        isDragActive ? 'border-primary-500 bg-primary-50' : 'border-secondary-300 hover:border-primary-400',
      )}
    >
      <input {...getInputProps()} />
      {files.length ? (
        <p className="text-sm font-medium text-secondary-700">
          <Upload className="mr-1 inline h-4 w-4" />  {t('Pridať ďalšie súbory (pretiahnite sem alebo kliknite)')}
        </p>
      ) : (
        <>
          <FileSpreadsheet className="mx-auto mb-3 h-12 w-12 text-secondary-400" />
          <p className="font-medium text-secondary-700">{t('Pretiahnite sem export transakcií zo Shell karty (CSV alebo XLSX) alebo kliknite pre výber')}</p>
          <p className="mx-auto mt-2 max-w-xl text-sm text-secondary-500">
            {t(
              'Súbor „transactions-…“ z portálu Shell / Fleetcor, napr. za mesiac alebo štvrťrok. Obdobia sa môžu prekrývať – už importované transakcie sa nepridajú druhýkrát, iba sa aktualizujú (napr. keď sú medzičasom vyfakturované). Pred importom uvidíte náhľad.',
            )}
          </p>
        </>
      )}
    </div>
  );

  if (!files.length) return <Card>{dropzone}</Card>;

  return (
    <div className="space-y-4">
      <Card>
        <ul className="mb-3 divide-y divide-secondary-100 text-sm">
          {files.map((f) => {
            const info = preview?.files.find((p) => p.file_name === f.name);
            return (
              <li key={f.name} className="flex items-center justify-between gap-3 py-2">
                <span className="flex min-w-0 items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-secondary-400" />
                  <span className="truncate">{f.name}</span>
                  <span className="shrink-0 text-xs text-secondary-500">{formatFileSize(f.size)}</span>
                  {info?.error ? <Badge variant="danger">{info.error}</Badge> : info && <Badge variant="success">{t('{rows} riadkov', { rows: info.rows })}</Badge>}
                </span>
                <button className="icon-btn" onClick={() => removeFile(f.name)} aria-label={t('Odobrať súbor')}>
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
        {dropzone}
      </Card>

      {preview && preview.total > 0 && (
        <Card>
          <CardHeader
            title={t('Náhľad importu')}
            subtitle={preview.date_from && t('Obdobie {from} – {to}', { from: formatDate(preview.date_from), to: formatDate(preview.date_to) })}
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label={t('Transakcie')} value={preview.total} />
            <Stat label={t('Nové')} value={preview.new} tone="text-green-700" />
            <Stat label={t('Aktualizované')} value={preview.updated} />
            <Stat label={t('Už importované')} value={preview.unchanged} tone="text-secondary-600" />
            <Stat label={t('Litre paliva')} value={preview.liters} />
          </div>

          {preview.unknown_plates.length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">
                {t('Tieto ŠPZ nie sú v systéme: {plates}', { plates: preview.unknown_plates.map((p) => `${p.license_plate} (${p.count}×)`).join(', ') })}
              </p>
              <Checkbox
                className="mt-2"
                label={t('Založiť chýbajúce vozidlá (inak sa ich transakcie preskočia)')}
                checked={createMissing}
                onChange={(e) => setCreateMissing(e.target.checked)}
              />
            </div>
          )}
          {preview.fuel_mismatches.length > 0 && (
            <details className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <summary className="cursor-pointer font-medium">
                {t('Iné palivo, než má vozidlo zapísané ({count}×) – skontrolujte druh paliva vozidla', { count: preview.fuel_mismatches.length })}
              </summary>
              <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto">
                {preview.fuel_mismatches.map((m, i) => (
                  <li key={i}>
                    {m.license_plate} · {formatDate(m.date)} · {m.product}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="mt-4 overflow-x-auto rounded-lg border border-secondary-200">
            <table className="w-full text-sm">
              <thead className="bg-secondary-50 text-left text-xs uppercase tracking-wide text-secondary-500">
                <tr>
                  <th className="px-3 py-2">{t('ŠPZ')}</th>
                  <th className="px-3 py-2 text-right">{t('Transakcie')}</th>
                  <th className="px-3 py-2 text-right">{t('Palivo')}</th>
                  <th className="px-3 py-2 text-right">{t('Bez DPH')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {preview.cars.map((c) => (
                  <tr key={c.license_plate} className={cn(!c.known && 'text-amber-700')}>
                    <td className="px-3 py-1.5 font-mono">{c.license_plate}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{c.count}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatLiters(c.liters)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(c.amount_net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ErrorList errors={preview.errors} />
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <p className="text-sm text-secondary-600">{tx('Spolu bez DPH {amount}', { amount: <strong>{formatCurrency(preview.amount_net)}</strong> })}</p>
            <Button onClick={runImport} loading={importMutation.isPending} disabled={!preview.new && !preview.updated && !(createMissing && preview.unknown_plates.length)}>
              <Upload className="h-4 w-4" />  {t('Potvrdiť import')}
            </Button>
          </div>
        </Card>
      )}
      {preview && preview.total === 0 && <ErrorList errors={preview.errors} />}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg bg-secondary-50 p-3 text-center">
      <p className={cn('text-2xl font-bold tabular-nums', tone ?? 'text-secondary-900')}>{formatNumber(value)}</p>
      <p className="text-xs text-secondary-500">{label}</p>
    </div>
  );
}

function ErrorList({ errors }: { errors: FuelImportError[] }) {
  if (!errors.length) return null;
  return (
    <details className="mt-4 w-full rounded-lg border border-red-200 bg-red-50 p-3 text-left text-sm text-red-800">
      <summary className="cursor-pointer font-medium">{t('Chyby ({count})', { count: errors.length })}</summary>
      <ul className="mt-2 max-h-48 space-y-0.5 overflow-y-auto">
        {errors.map((e, i) => (
          <li key={`${e.file_name}-${e.row}-${i}`}>
            {e.row ? t('{file}, riadok {row}: {errors}', { file: e.file_name, row: e.row, errors: e.errors.join('; ') }) : `${e.file_name}: ${e.errors.join('; ')}`}
          </li>
        ))}
      </ul>
    </details>
  );
}
